import { FC, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  StackProps,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useChannel } from 'ably/react';

import { UnitApi } from '@/api';
import { IcBox, IcRefresh, IcRoadAssistance, IcUsers } from '@/assets/images';
import { EditableRequiredUnits, SearchBar } from '@/components';
import UnitStatusTab from '@/components/Modals/UnitShiftListModal/components/UnitStatusTab';
import { LocalStorageItems } from '@/constants';
import { WithLoaderProps } from '@/hocs';
import { useCFSContext, useUnitShiftContext } from '@/hooks';
import {
  AssignedUnitShiftStatus,
  IAssignedUnitShift,
  IUnit,
  IUnitShift,
  IUser,
  UnitShiftStatus,
} from '@/models';
import { isCFSViewOnly } from '@/services';
import { useDataStore, useToastStore, useUserStore } from '@/store';
import { formatPersonName } from '@/utils';
import { searchByResources } from '@/utils/searchByResources';

import { UnitShiftListRoot } from '../styles';
import AssignedUnits from './AssignedUnits';
import AvailableUnits from './AvailableUnits';
import UnitShiftLogs from './Logs';
import OffDutyUnits from './OffDutyUnits';
import { TabPanel } from './TabPanel';
import { UnitFilter } from './UnitFilter';
import { FilterValues } from './UnitFilterForm';

interface UnitShiftListProps extends StackProps {
  cfsId?: string;
  fetchActiveCFSList?: () => void;
  isUnitShiftsPage?: boolean;
  isAccountWideView?: boolean;
  isDispatcher?: boolean;
}

export interface CommonUnitsProps extends WithLoaderProps {
  cfsId?: string;
  filterValues: FilterValues;
  searchText?: string;
  handleAssignUnit?: () => void;
}

interface AssignedUnitWithCFS {
  unit: IUnit;
  matchedBadgeUser?: {
    badgeNumber?: string;
    employeeName: string;
    matchType: string;
  };
  matchedVehicleName?: string;
  matchedEquipmentName?: string;
  assignments: Array<{
    cfsId: string;
    cfsNumber: string;
  }>;
}

interface GroupedSearchResults {
  available: {
    units: IUnitShift[];
    count: number;
  };
  scheduled: {
    units: IUnitShift[];
    count: number;
  };
  assigned: {
    units: AssignedUnitWithCFS[];
    count: number;
  };
  offDuty: {
    units: IUnit[];
    count: number;
  };
  standby: {
    units: IUnit[];
    count: number;
  };
}

// Helper function to check if a unit is currently assigned
const isCurrentlyAssigned = (assigned: IAssignedUnitShift) => {
  return (
    !assigned.unAssignedAt &&
    assigned.status !== AssignedUnitShiftStatus.CANCELED &&
    assigned.status !== AssignedUnitShiftStatus.COMPLETED
  );
};

const EVENT_MAPPER = {
  'On Scene': 'UNIT_SHIFT_IS_ON_SCENE',
  Assigned: 'UNIT_SHIFT_ASSIGNED',
  'En Route': 'UNIT_SHIFT_IS_EN_ROUTE',
  'In Progress': 'UNIT_SHIFT_IS_IN_PROGRESS',
  Canceled: 'UNIT_SHIFT_CANCELED',
  Completed: 'UNIT_SHIFT_COMPLETED',
  Closed: 'CLOSED',
  'In The Area': 'UNIT_SHIFT_IS_IN_THE_AREA',
} as const;

export const UnitShiftList: FC<UnitShiftListProps> = ({
  cfsId,
  fetchActiveCFSList,
  isUnitShiftsPage,
  isAccountWideView: _isAccountWideView,
  isDispatcher = false,
  ...rest
}) => {
  const { handleRefresh, cfs } = useCFSContext();
  const { filterValues } = useUnitShiftContext();
  const { fetchBeats } = useDataStore();
  const { updateToast } = useToastStore();
  const { account, user: currentUser } = useUserStore();
  const isViewOnly = isCFSViewOnly(currentUser);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const location = useLocation();
  const isCustomStatusFeatureEnabled =
    account?.settings?.features?.isEnabledCustomUnitStatus;

  // Determine initial tab, considering dispatcher status
  const initialTab = useMemo(() => {
    const savedTab = Number(
      localStorage.getItem(LocalStorageItems.UnitShiftListCurrentTab),
    );
    // If custom status feature is disabled, ignore saved tab 0
    const validSavedTab = isCustomStatusFeatureEnabled
      ? savedTab
      : savedTab === 0
      ? NaN
      : savedTab;

    if (isDispatcher && isCustomStatusFeatureEnabled && validSavedTab === 0)
      return 0;
    if (
      !isNaN(validSavedTab) &&
      validSavedTab > 0 &&
      validSavedTab <= (isUnitShiftsPage ? 4 : 3)
    )
      return validSavedTab;
    return 1; // Default to Available Units
  }, [isDispatcher, isCustomStatusFeatureEnabled, isUnitShiftsPage]);

  const [currentTab, setCurrentTab] = useState<number>(initialTab);

  const [searchUnitText, setSearchUnitText] = useState<string>('');
  const [searchCancelToken, setSearchCancelToken] = useState<string>('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [offDutyUnits, setOffDutyUnits] = useState<IUnit[]>([]);

  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [activeLogsFilter, setActiveLogsFilter] = useState<any>(undefined);
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>('');
  const hasFetchedLogs = useRef(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [useScrollableTabs, setUseScrollableTabs] = useState(false);

  const {
    onDutyUnitShifts,
    scheduledUnitShifts,
    assignedUnitShifts,
    setSearchText,
    fetchAllActiveUnitShifts,
  } = useUnitShiftContext();

  const handleAgencyId = () => {
    const params = new URLSearchParams(window.location.search);
    const agencyId = params.get('agencyId');

    if (agencyId) {
      localStorage.setItem(LocalStorageItems.CurrentAgencyId, agencyId);
    }
  };

  const fetchOffDutyUnits = async () => {
    try {
      const filter = JSON.stringify({
        status: 'OFF_DUTY',
      });
      const res = await UnitApi.getUnits({ filter, limit: 200 });

      setOffDutyUnits(res.data.results);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  useChannel(`account:${account?._id}:unit`, () => {
    if (!cfsId) {
      fetchOffDutyUnits();
    }
  });

  useEffect(() => {
    handleAgencyId();
    fetchBeats();
    if (!cfsId) {
      fetchOffDutyUnits();
    }
    fetchAllActiveUnitShifts();

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchResultsRef.current &&
        !searchResultsRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const _searchByResources = useCallback(searchByResources, []);
  const getMatchedBadgeUser = useCallback(
    (unitShift?: IUnitShift, searchValue?: string) => {
      const normalizedSearch = searchValue?.trim().toLowerCase();
      if (!unitShift || !normalizedSearch) return undefined;

      const matchedByBadgeUser = unitShift.users?.find((user) =>
        user.profile?.employmentInformation?.badgeNumber
          ?.toLowerCase()
          .includes(normalizedSearch),
      );
      const matchedByNameUser = unitShift.users?.find((user) =>
        formatPersonName(user).trim().toLowerCase().includes(normalizedSearch),
      );
      const matchedUser = matchedByBadgeUser ?? matchedByNameUser;
      if (!matchedUser) return undefined;

      const badgeNumber =
        matchedUser.profile?.employmentInformation?.badgeNumber;
      const employeeName = formatPersonName(matchedUser);
      return {
        badgeNumber,
        employeeName,
        matchType: matchedByBadgeUser ? 'badgeNumber' : 'employeeName',
      };
    },
    [],
  );
  const getMatchedEquipmentName = useCallback(
    (unitShift?: IUnitShift, searchValue?: string) => {
      const normalizedSearch = searchValue?.trim().toLowerCase();
      if (!unitShift || !normalizedSearch) return undefined;

      return unitShift.equipments?.find((equipment) =>
        equipment.name?.toLowerCase().includes(normalizedSearch),
      )?.name;
    },
    [],
  );
  const getMatchedVehicleName = useCallback(
    (unitShift?: IUnitShift, searchValue?: string) => {
      const normalizedSearch = searchValue?.trim().toLowerCase();
      if (!unitShift || !normalizedSearch) return undefined;

      return unitShift.vehicles?.find((vehicle) =>
        vehicle.name?.toLowerCase().includes(normalizedSearch),
      )?.name;
    },
    [],
  );

  const groupedResults = useMemo<GroupedSearchResults>(() => {
    const empty: GroupedSearchResults = {
      available: { units: [], count: 0 },
      scheduled: { units: [], count: 0 },
      assigned: { units: [], count: 0 },
      offDuty: { units: [], count: 0 },
      standby: { units: [], count: 0 },
    };

    const searchValue = searchUnitText.trim();
    if (!searchValue) {
      return empty;
    }

    const searchTerm = searchValue.toLowerCase();

    const searchUnitShifts = (unit: IUnit) =>
      unit.name?.toLowerCase().includes(searchTerm) ||
      unit.callSign?.toLowerCase().includes(searchTerm) ||
      unit.users?.some((u) =>
        (u as IUser)?.profile?.employmentInformation?.badgeNumber?.includes(
          searchTerm,
        ),
      );

    const currentlyAssignedUnitIds = new Set(
      assignedUnitShifts
        .filter(isCurrentlyAssigned)
        .map((assigned) => assigned.unitShift.unit._id),
    );

    const unitShiftSortFn = (
      a: IUnitShift | AssignedUnitWithCFS,
      b: IUnitShift | AssignedUnitWithCFS,
    ) => {
      if (!cfs?.assignedAgencies) return 0;
      return (
        Number(cfs.assignedAgencies.includes(b.unit.agency)) -
        Number(cfs.assignedAgencies.includes(a.unit.agency))
      );
    };

    const unitSortFn = (a: IUnit, b: IUnit) => {
      if (!cfs?.assignedAgencies) return 0;
      return (
        Number(cfs.assignedAgencies.includes(b.agency)) -
        Number(cfs.assignedAgencies.includes(a.agency))
      );
    };

    const isOpenShift = (s: IUnitShift) =>
      (searchUnitShifts(s.unit) || _searchByResources(s, searchValue)) &&
      !currentlyAssignedUnitIds.has(s.unit._id) &&
      s.status !== UnitShiftStatus.CLOSED;

    const availableResults = onDutyUnitShifts
      .filter(isOpenShift)
      .sort(unitShiftSortFn);
    const scheduledResults = scheduledUnitShifts
      .filter(isOpenShift)
      .sort(unitShiftSortFn);

    const assignedUnitsMap = new Map<string, AssignedUnitWithCFS>();
    assignedUnitShifts.filter(isCurrentlyAssigned).forEach((assigned) => {
      const { unit } = assigned.unitShift;
      const unitShift = assigned.unitShift;
      if (
        !searchUnitShifts(unit) &&
        !_searchByResources(unitShift, searchValue)
      ) {
        return;
      }

      const assignment = {
        cfsId: assigned.cfsId ?? '',
        cfsNumber: assigned.cfsNumber ?? '',
      };
      const existing = assignedUnitsMap.get(unit._id);
      if (existing) {
        existing.assignments.push(assignment);
        existing.matchedBadgeUser ??= getMatchedBadgeUser(
          unitShift,
          searchValue,
        );
        existing.matchedVehicleName ??= getMatchedVehicleName(
          unitShift,
          searchValue,
        );
        existing.matchedEquipmentName ??= getMatchedEquipmentName(
          unitShift,
          searchValue,
        );
      } else {
        assignedUnitsMap.set(unit._id, {
          unit,
          matchedBadgeUser: getMatchedBadgeUser(unitShift, searchValue),
          matchedVehicleName: getMatchedVehicleName(unitShift, searchValue),
          matchedEquipmentName: getMatchedEquipmentName(unitShift, searchValue),
          assignments: [assignment],
        });
      }
    });
    const assignedResults = Array.from(assignedUnitsMap.values()).sort(
      unitShiftSortFn,
    );

    const standbyResults = offDutyUnits
      .filter(
        (unit) => searchUnitShifts(unit) && unit.isStandByWhenOffDuty === true,
      )
      .sort(unitSortFn);
    const offDutyResults = cfsId
      ? []
      : offDutyUnits
          .filter(
            (unit) =>
              searchUnitShifts(unit) && unit.isStandByWhenOffDuty !== true,
          )
          .sort(unitSortFn);

    return {
      available: {
        units: availableResults,
        count: availableResults.length,
      },
      scheduled: {
        units: scheduledResults,
        count: scheduledResults.length,
      },
      assigned: {
        units: assignedResults,
        count: assignedResults.length,
      },
      offDuty: {
        units: offDutyResults,
        count: offDutyResults.length,
      },
      standby: {
        units: standbyResults,
        count: standbyResults.length,
      },
    };
  }, [
    searchUnitText,
    onDutyUnitShifts,
    scheduledUnitShifts,
    assignedUnitShifts,
    offDutyUnits,
    cfs?.assignedAgencies,
    cfsId,
    _searchByResources,
    getMatchedBadgeUser,
    getMatchedVehicleName,
    getMatchedEquipmentName,
  ]);

  const handleSearch = (searchValue: string) => {
    setSearchUnitText(searchValue);
    setSearchText(searchValue);
    setShowSearchResults(!!searchValue.trim());
  };

  const handleResultClick = (unit: IUnit, section: string) => {
    setSearchUnitText(unit.name || unit.callSign || '');
    setShowSearchResults(false);

    let targetTab = -1;
    switch (section) {
      case 'available':
      case 'scheduled':
      case 'standby':
        targetTab = 1; // Staging tab 0 maps to original index 1 (Available)
        break;
      case 'assigned':
        targetTab = 2; // Staging tab 1 maps to original index 2 (Assigned)
        break;
      case 'offDuty':
        targetTab = 3; // Staging tab 2 maps to original index 3 (Off Duty)
        break;
      default:
        targetTab = 1; // Default to Available
    }

    // Ensure the target tab is actually visible before changing
    if (getVisibleIndex(targetTab) !== -1) {
      setCurrentTab(targetTab);
      localStorage.setItem(
        LocalStorageItems.UnitShiftListCurrentTab,
        String(targetTab),
      );
    } else {
      // If target is not visible, default to a visible tab (e.g., Available)
      const defaultVisibleTab = 1;
      setCurrentTab(defaultVisibleTab);
      localStorage.setItem(
        LocalStorageItems.UnitShiftListCurrentTab,
        String(defaultVisibleTab),
      );
    }
  };

  const handleTabChange = (newVisibleIndex: number) => {
    const originalIndex = getOriginalIndex(newVisibleIndex);
    setCurrentTab(originalIndex);
    localStorage.setItem(
      LocalStorageItems.UnitShiftListCurrentTab,
      String(originalIndex),
    );
    setShowSearchResults(false);
  };

  const handleRefreshUnitShiftDistances = () => {
    handleRefresh();
  };

  const handleSearchFocus = () => {
    if (!searchUnitText.trim()) return;
    handleSearch(searchUnitText);
  };

  const handleAssignUnit = () => {
    // Clear search input and results when a UnitItem action occurs
    setSearchUnitText('');
    setShowSearchResults(false);
    setSearchText('');
    // Trigger SearchBar internal clear via shouldCancel token
    setSearchCancelToken(String(Date.now()));
  };

  const fetchData = async (
    pageNumber: number,
    limit: number,
    logsFilter?: { event: string; unit: string },
    searchValue = '',
  ) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const agencyId = params.get('agencyId');

      const filter = JSON.stringify({
        ...(logsFilter?.event
          ? {
              event:
                EVENT_MAPPER[logsFilter?.event as keyof typeof EVENT_MAPPER],
            }
          : {}),
        ...(logsFilter?.unit ? { unit: logsFilter.unit } : {}),
      });

      const response = await UnitApi.getAllUnitShiftLogs({
        page: pageNumber + 1,
        limit,
        ...(searchValue ? { search: searchValue } : {}),
        ...(agencyId ? { agencyId } : {}),
        filter,
      });

      setData(response.data.docs);
      setTotalCount(response.data.totalDocs);
      setIsLoading(false);
    } catch (error) {
      setData([]);
      setTotalCount(0);
      setIsLoading(false);
    }
  };

  const handleFilter = (pageNumber: number, limit: number, filterData: any) => {
    setActiveLogsFilter(filterData);
    setActiveSearchTerm('');
    setPage(pageNumber);
    fetchData(pageNumber, limit, filterData);
  };

  const handleSearchLogs = (searchValue: string) => {
    setActiveSearchTerm(searchValue);
    setActiveLogsFilter(undefined);
    setPage(0);
    fetchData(0, rowsPerPage, undefined, searchValue);
  };

  const handlePageChange = (
    newPage: number,
    limit = rowsPerPage,
    filter = activeLogsFilter,
    search = activeSearchTerm,
  ) => {
    setPage(newPage);
    fetchData(newPage, limit, filter, search);
  };

  const handleRowsPerPageChange = (
    pageNumber: number,
    newRowsPerPage: number,
    filter = activeLogsFilter,
    search = activeSearchTerm,
  ) => {
    setRowsPerPage(newRowsPerPage);
    setPage(pageNumber);
    fetchData(pageNumber, newRowsPerPage, filter, search);
  };

  useEffect(() => {
    const visibleIndex = getVisibleIndex(currentTab);
    if (visibleIndex === -1) {
      // Current tab is no longer visible, default to Available (index 1)
      setCurrentTab(1);
      localStorage.setItem(LocalStorageItems.UnitShiftListCurrentTab, '1');
    }
  }, [isDispatcher]);

  // Check if the current page is the agency-specific unit shifts view
  const isAgencyUnitShiftsPage = useMemo(() => {
    const agencyPathRegex = /^\/agencies\/[^/]+\/units\/unit-shifts$/;
    return (
      agencyPathRegex.test(location.pathname) &&
      new URLSearchParams(location.search).has('agencyId')
    );
  }, [location.pathname, location.search]);

  const TABS = [
    {
      index: 1,
      label: 'Available',
      component: AvailableUnits,
      condition: true,
    },
    {
      index: 2,
      label: 'Assigned',
      component: AssignedUnits,
      condition: true,
    },
    {
      index: 3,
      label: 'Off Duty',
      component: OffDutyUnits,
      condition: !cfsId,
    },
    {
      index: 5,
      label: 'Status',
      component: UnitStatusTab,
      condition:
        isDispatcher && isCustomStatusFeatureEnabled && !isAgencyUnitShiftsPage,
    },
    {
      index: 4,
      label: 'Logs',
      component: UnitShiftLogs,
      condition: isUnitShiftsPage,
    },
  ];

  const visibleTabs = TABS.filter((tab) => tab.condition);

  // Function to map original index to visible index
  const getVisibleIndex = (originalIndex: number): number => {
    // If the status tab feature is disabled, original index 0 is never visible
    if (!(isDispatcher && isCustomStatusFeatureEnabled) && originalIndex === 5)
      return -1;
    let visibleIdx = -1;
    let count = 0;
    for (const tab of TABS) {
      if (tab.condition) {
        if (tab.index === originalIndex) {
          visibleIdx = count;
          break;
        }
        count++;
      }
    }
    return visibleIdx;
  };

  const getOriginalIndex = (visibleIndex: number): number => {
    let count = 0;
    for (const tab of TABS) {
      if (tab.condition) {
        if (count === visibleIndex) {
          return tab.index;
        }
        count++;
      }
    }
    return -1;
  };

  const assignedTabVisibleIndex = getVisibleIndex(2);

  // Function to check if tabs need scrollable variant
  const checkTabsOverflow = useCallback(() => {
    if (tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const tabsElement = container.querySelector('.MuiTabs-flexContainer');
      if (tabsElement) {
        const containerWidth = container.clientWidth;
        const tabsWidth = tabsElement.scrollWidth;
        setUseScrollableTabs(tabsWidth > containerWidth);
      }
    }
  }, []);

  // Check tabs overflow on mount and when tabs change
  useEffect(() => {
    checkTabsOverflow();
    const resizeObserver = new ResizeObserver(checkTabsOverflow);
    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [checkTabsOverflow, visibleTabs.length]);

  // Define default empty handlers for props
  const defaultPageChange = () => {};
  const defaultRowsPerPageChange = () => {};
  const defaultSearch = () => {};

  useEffect(() => {
    if (isUnitShiftsPage && !hasFetchedLogs.current) {
      fetchData(page, rowsPerPage, activeLogsFilter, activeSearchTerm);
      hasFetchedLogs.current = true;
    }
  }, [isUnitShiftsPage]);

  return (
    <UnitShiftListRoot className="unit-list" {...rest} position="relative">
      {/* Top Area for Search, Filter, Refresh, and Required Units */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        {/* Render Required Units first if cfsId exists, wrapped in a Box for spacing */}
        {cfsId && <EditableRequiredUnits />}
      </Box>

      {showSearchResults && (
        <Paper
          ref={searchResultsRef}
          elevation={3}
          className="search-unit-dropdown"
          sx={{
            position: 'absolute',
            left: '8px',
            right: '8px',
            zIndex: 1201, // Above modal content
            maxHeight: '400px',
            overflowY: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          {/* Available Section */}
          {groupedResults.available.count > 0 && (
            <Box key="available" sx={{ mb: 1 }}>
              <Typography color="error" sx={{ px: 2, py: 1 }}>
                Available ({groupedResults.available.count})
              </Typography>
              {groupedResults.available.units.map((unitShift) => {
                const matchedBadgeUser = getMatchedBadgeUser(
                  unitShift,
                  searchUnitText,
                );
                const matchedVehicleName = getMatchedVehicleName(
                  unitShift,
                  searchUnitText,
                );
                const matchedEquipmentName = getMatchedEquipmentName(
                  unitShift,
                  searchUnitText,
                );

                return (
                  <MenuItem
                    key={unitShift._id}
                    onClick={() =>
                      handleResultClick(unitShift.unit, 'available')
                    }
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      columnGap: 0.5,
                      flexWrap: 'wrap',
                      width: '100%',
                    }}
                  >
                    <Typography fontWeight={500}>
                      {unitShift.unit.name}
                    </Typography>
                    {matchedBadgeUser && (
                      <Box className="matched-chip">
                        <IcUsers />
                        <Typography variant="body2" color="text.secondary">
                          {`${
                            matchedBadgeUser.badgeNumber
                              ? `${matchedBadgeUser.badgeNumber} `
                              : ''
                          }${matchedBadgeUser.employeeName}`}
                        </Typography>
                      </Box>
                    )}
                    {matchedVehicleName && (
                      <Box className="matched-chip">
                        <IcRoadAssistance />
                        <Typography variant="body2" color="text.secondary">
                          {matchedVehicleName}
                        </Typography>
                      </Box>
                    )}
                    {matchedEquipmentName && (
                      <Box className="matched-chip">
                        <IcBox />
                        <Typography variant="body2" color="text.secondary">
                          {matchedEquipmentName}
                        </Typography>
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Box>
          )}

          {/* Scheduled Section - Rendered similarly to Available in staging */}
          {groupedResults.scheduled.count > 0 && (
            <Box key="scheduled" sx={{ mb: 1 }}>
              <Typography color="error" sx={{ px: 2, py: 1 }}>
                Scheduled ({groupedResults.scheduled.count}){' '}
                {/* Label adjusted */}
              </Typography>
              {groupedResults.scheduled.units.map((unitShift) => {
                const matchedBadgeUser = getMatchedBadgeUser(
                  unitShift,
                  searchUnitText,
                );
                const matchedVehicleName = getMatchedVehicleName(
                  unitShift,
                  searchUnitText,
                );
                const matchedEquipmentName = getMatchedEquipmentName(
                  unitShift,
                  searchUnitText,
                );

                return (
                  <MenuItem
                    key={unitShift._id}
                    onClick={() =>
                      handleResultClick(unitShift.unit, 'scheduled')
                    }
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      columnGap: 0.5,
                      flexWrap: 'wrap',
                      width: '100%',
                    }}
                  >
                    <Typography>{unitShift.unit.name}</Typography>
                    {matchedBadgeUser && (
                      <Box className="matched-chip">
                        <IcUsers />
                        <Typography variant="body2" color="text.secondary">
                          {`${
                            matchedBadgeUser.badgeNumber
                              ? `${matchedBadgeUser.badgeNumber} `
                              : ''
                          }${matchedBadgeUser.employeeName}`}
                        </Typography>
                      </Box>
                    )}
                    {matchedVehicleName && (
                      <Box className="matched-chip">
                        <IcRoadAssistance />
                        <Typography variant="body2" color="text.secondary">
                          {matchedVehicleName}
                        </Typography>
                      </Box>
                    )}
                    {matchedEquipmentName && (
                      <Box className="matched-chip">
                        <IcBox />
                        <Typography variant="body2" color="text.secondary">
                          {matchedEquipmentName}
                        </Typography>
                      </Box>
                    )}
                  </MenuItem>
                );
              })}
            </Box>
          )}

          {/* Assigned Section */}
          {groupedResults.assigned.count > 0 && (
            <Box key="assigned" sx={{ mb: 1 }}>
              <Typography color="error" sx={{ px: 2, py: 1 }}>
                Assigned ({groupedResults.assigned.count})
              </Typography>
              {groupedResults.assigned.units.map((item) => (
                <MenuItem
                  key={item.unit._id}
                  onClick={() => handleResultClick(item.unit, 'assigned')}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                    spacing={1}
                    sx={{ flexWrap: 'wrap', width: '100%' }}
                  >
                    <Typography fontWeight={500}>{item.unit.name}</Typography>
                    {item.matchedBadgeUser && (
                      <Box className="matched-chip">
                        <IcUsers />
                        <Typography variant="body2" color="text.secondary">
                          {`${
                            item.matchedBadgeUser.badgeNumber
                              ? `${item.matchedBadgeUser.badgeNumber} `
                              : ''
                          }${item.matchedBadgeUser.employeeName}`}
                        </Typography>
                      </Box>
                    )}
                    {item.matchedVehicleName && (
                      <Box className="matched-chip">
                        <IcRoadAssistance />
                        <Typography variant="body2" color="text.secondary">
                          {item.matchedVehicleName}
                        </Typography>
                      </Box>
                    )}
                    {item.matchedEquipmentName && (
                      <Box className="matched-chip">
                        <IcBox />
                        <Typography variant="body2" color="text.secondary">
                          {item.matchedEquipmentName}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Assigned to:{' '}
                    {item.assignments.map((a) => `#${a.cfsNumber}`).join(', ')}
                  </Typography>
                </MenuItem>
              ))}
            </Box>
          )}

          {/* Standby Section - Render if results exist (no cfsId check) */}
          {groupedResults.standby.count > 0 && (
            <Box key="standby" sx={{ mb: 1 }}>
              <Typography color="error" sx={{ px: 2, py: 1 }}>
                Standby ({groupedResults.standby.count})
              </Typography>
              {groupedResults.standby.units.map((unit) => (
                <MenuItem
                  key={unit._id}
                  onClick={() => handleResultClick(unit, 'standby')}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  {unit.name}
                </MenuItem>
              ))}
            </Box>
          )}

          {/* Off Duty Section - Only show if !cfsId */}
          {!cfsId && groupedResults.offDuty.count > 0 && (
            <Box key="offDuty" sx={{ mb: 1 }}>
              <Typography color="error" sx={{ px: 2, py: 1 }}>
                Off Duty ({groupedResults.offDuty.count})
              </Typography>
              {groupedResults.offDuty.units.map((unit) => (
                <MenuItem
                  key={unit._id}
                  onClick={() => handleResultClick(unit, 'offDuty')}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  {unit.name}
                </MenuItem>
              ))}
            </Box>
          )}

          {/* No Results Message */}
          {Object.values(groupedResults).every((res) => res.count === 0) && (
            <Typography
              sx={{ p: 2, textAlign: 'center' }}
              color="text.secondary"
            >
              No units found.
            </Typography>
          )}
        </Paper>
      )}
      <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
        {/* Row for Search, Filter, Refresh */}
        <Stack direction="row" spacing={1} alignItems="center">
          {' '}
          <SearchBar
            placeholder="Search Unit/Resource"
            onChange={handleSearch}
            onFocus={handleSearchFocus}
            value={searchUnitText}
            shouldCancel={searchCancelToken}
            fullWidth
            className="search-unit"
          />
          <UnitFilter />
          {!isViewOnly &&
            getVisibleIndex(currentTab) === assignedTabVisibleIndex && (
              <IconButton
                onClick={handleRefreshUnitShiftDistances}
                size="small"
                aria-label="Refresh distances"
              >
                <IcRefresh />
              </IconButton>
            )}
        </Stack>
      </Box>
      {/* Tabs */}
      <Tabs
        ref={tabsContainerRef}
        value={getVisibleIndex(currentTab)}
        onChange={(_event, newVisibleIndex) => handleTabChange(newVisibleIndex)}
        variant={useScrollableTabs ? 'scrollable' : 'fullWidth'}
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', overflowY: 'hidden' }}
      >
        {visibleTabs.map((tab, visibleIndex) => (
          <Tab key={visibleIndex} label={tab.label} />
        ))}
      </Tabs>

      {/* Tab Content */}
      <Box className="content" flexGrow={1} overflow="auto">
        {visibleTabs.map((tab, visibleIndex) => {
          const commonProps = {
            cfsId,
            filterValues,
            searchText: searchUnitText,
            handleAssignUnit,
            setCurrentTab: handleTabChange,
            fetchActiveCFSList,
            showMenu: !cfsId,
            showDraggableUnits: true,
          };

          const logSpecificProps =
            tab.index === 4
              ? {
                  data,
                  page,
                  rowsPerPage,
                  count: totalCount,
                  isLoading,
                  onPageChange: handlePageChange,
                  onRowsPerPageChange: handleRowsPerPageChange,
                  onSearch: handleSearchLogs,
                  onFilter: handleFilter,
                }
              : {
                  data: [],
                  page: 0,
                  rowsPerPage: 0,
                  count: 0,
                  isLoading: false,
                  onPageChange: defaultPageChange,
                  onRowsPerPageChange: defaultRowsPerPageChange,
                  onSearch: defaultSearch,
                };

          return (
            <TabPanel
              key={tab.index}
              value={getVisibleIndex(currentTab)}
              index={visibleIndex}
            >
              <tab.component {...commonProps} {...logSpecificProps} />
            </TabPanel>
          );
        })}
      </Box>
    </UnitShiftListRoot>
  );
};
